import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { State, getAllCitiesByZipCode } from '@app/app.reducer';
import { Store, select } from '@ngrx/store';
import { HelperService } from '@store/helpers/helper.service';
import { BehaviorSubject, Observable, Subject, from, of } from 'rxjs';
import { catchError, debounceTime, first, map, mergeMap } from 'rxjs/operators';
import {
  EmailValidationErrors,
  PhoneValidationErrors,
  RequiredValidationError,
  ValueComparisonValidationErrors,
  ZipValidationErrors
} from './validation-errors.model';
import { RegexPatterns } from './validation-patterns.const';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  formCountryInputChangedBH$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  formCountryInputChanged$: Observable<string> = this.formCountryInputChangedBH$.asObservable();
  private zipLast: {
    value: any;
    country: any;
    validation: ZipValidationErrors;
    formPath: string;
    zipCodeCities: string[];
  } = {
    value: null,
    country: null,
    validation: null,
    formPath: null,
    zipCodeCities: []
  };
  private zipValidationSubject = new Subject();
  private zipValidationPromise: Promise<ZipValidationErrors>;
  private formCountryInputChanged = false;

  constructor(
    private helperService: HelperService,
    private store: Store<State>
  ) {
    this.resetZipValidationPromise();
  }

  static emailValidator(isRequired: boolean): ValidatorFn {
    return (control: AbstractControl): EmailValidationErrors => {
      const controlVal = control.value;

      return this.emailValidatorFn(controlVal, isRequired);
    };
  }

  static emailValidatorFn(controlVal: string, isRequired: boolean): EmailValidationErrors {
    if (!controlVal) {
      return null;
    }

    const emailCanContainRegex = new RegExp(RegexPatterns.email);
    // additional invalid characters
    const emailCannotContainRegex = new RegExp('[ßæ¥þ‰¼><=]+');
    const umlautRegex = new RegExp('[äöüëïöüÿÄÖÜËÏÖÜŸ]+');
    const matchPattern = controlVal.match(emailCanContainRegex);
    const containsAdditional = emailCannotContainRegex.test(controlVal);
    // eslint-disable-next-line no-control-regex
    const nonAsciiChars = new RegExp('[^\x00-\x7F]+').test(controlVal);

    let validationError: EmailValidationErrors = null;

    if (umlautRegex.test(controlVal)) {
      validationError = { umlautCharacter: true };
    } else if (!matchPattern || containsAdditional || controlVal.length > 100 || nonAsciiChars) {
      validationError = { invalidEmailAddress: true };
    } else if (matchPattern) {
      validationError = null;
    } else if (!controlVal && !isRequired) {
      validationError = null;
    }

    return validationError;
  }

  static phoneValidator(): ValidatorFn {
    return (control: AbstractControl): PhoneValidationErrors => {
      const value = control.value;

      return this.phoneValidatorFn(value);
    };
  }

  static phoneValidatorFn(value: string | number): PhoneValidationErrors {
    const testMinSix = new RegExp(RegexPatterns.phoneLength);
    const valueAsString = value && value.toString();

    let validationError: PhoneValidationErrors = { invalidPhoneNumber: true };
    // const testSpaces = new RegExp(RegexPatterns.phoneSpaces);

    // it need to be either empty or fit to patern
    if (
      !value ||
      testMinSix.test(valueAsString.replace(/\s/g, '')) //&& testSpaces.test(value))
    ) {
      validationError = null;
    }

    return validationError;
  }

  static checkboxGroupValidator(form: UntypedFormGroup) {
    const isAnyChecked = Object.keys(form.controls)
      .map(function (key) {
        return form.controls[key].value;
      })
      .reduce((aggr, current) => {
        return aggr || current;
      }, false);

    return isAnyChecked ? null : { noneChecked: true };
  }

  static equalValueValidator(controlNameOne: string, controlNameTwo: string, caseInSensitive: boolean): ValidatorFn {
    return (group: UntypedFormGroup): ValueComparisonValidationErrors => {
      const controlOne = group.controls[controlNameOne];
      const controlTwo = group.controls[controlNameTwo];

      let isMatching: boolean;
      let validationError: ValueComparisonValidationErrors = null;

      if (!!controlOne && !!controlTwo && controlOne.value && controlTwo.value) {
        // only validate if both inputs have value
        if (caseInSensitive) {
          isMatching = controlOne.value.toLowerCase() === controlTwo.value.toLowerCase();
        } else {
          isMatching = controlOne.value === controlTwo.value;
        }

        // se the propper messages for the two controls
        if (!isMatching && controlOne.valid && controlTwo.valid) {
          const message = `${controlNameOne} != ${controlNameTwo}`;

          controlTwo.setErrors({ equalValue: controlNameTwo });

          validationError = { equalValue: message };
        }
        if (isMatching && controlTwo.hasError('equalValue')) {
          controlTwo.setErrors(null);
        }
      }

      return validationError;
    };
  }

  static compareValidator(controlNameOne: string, controlNameTwo: string): ValidatorFn {
    return (control: AbstractControl): ValueComparisonValidationErrors => {
      let result: ValueComparisonValidationErrors = null;

      if (control.parent) {
        const newValue = control.parent.controls[controlNameOne].value;
        const repeatValue = control.parent.controls[controlNameTwo].value;

        if (newValue !== repeatValue) {
          result = { different: true };
        }

        if (newValue === '' || repeatValue === '') {
          result = null;
        }
      } else {
        result = { different: true };
      }
      return result;
    };
  }

  resetZipValidationPromise() {
    this.zipValidationPromise = this.zipValidationSubject
      .pipe(
        debounceTime(500),
        mergeMap(
          (data: { selectedCountryCode: string; inputValue: string; formPath?: string; isCountryChange?: boolean }) => {
            const { selectedCountryCode, inputValue, formPath, isCountryChange } = data;

            let wasDataFoundInStore = false;
            // find if we already have data with same parameters in store
            this.store.pipe(select(getAllCitiesByZipCode), first()).subscribe((zipCodeCities) => {
              if (zipCodeCities) {
                // set wasDataFoundInStore if same values were found
                wasDataFoundInStore = zipCodeCities.some((zipCodeCity) => {
                  if (
                    selectedCountryCode === zipCodeCity.countryCode &&
                    inputValue.length !== zipCodeCity.zipCode.length &&
                    zipCodeCity.zipCode !== '' &&
                    !zipCodeCity.isZipCodeInvalid &&
                    zipCodeCity.zipCodeHasValidation
                  ) {
                    this.zipLast.validation = { invalidZipCode: true };

                    return true;
                  }
                  // if we already have saved results with same zipcode and countrycode set zipLast values
                  if (
                    !!zipCodeCity.countryCode &&
                    zipCodeCity.zipCode === inputValue &&
                    zipCodeCity.countryCode === selectedCountryCode
                  ) {
                    this.zipLast.value = inputValue;
                    this.zipLast.country = selectedCountryCode;
                    this.zipLast.zipCodeCities = zipCodeCity.cities;
                    this.zipLast.formPath = '';
                    this.zipLast.validation = zipCodeCity.isZipCodeInvalid ? { invalidZipCode: true } : null;

                    // if data was found return true and exit loop
                    return true;
                  }
                });
              }
            });
            // if data was found, we don't have to check our database for data because we already have it in store
            // Set zipLast.validation to null and return empty observable
            if (wasDataFoundInStore) {
              if (isCountryChange) {
                this.formCountryInputChanged = false;
                this.formCountryInputChangedBH$.next(formPath);
              }
              return of(this.zipLast.validation);
            }

            return this.helperService.checkZipcodeValidity(selectedCountryCode, inputValue).pipe(
              map((response) => {
                if (response.isValidZipCode) {
                  this.zipLast.validation = null;
                } else {
                  this.zipLast.validation = { invalidZipCode: true };
                }

                this.zipLast.value = inputValue;
                this.zipLast.country = selectedCountryCode;
                this.zipLast.formPath = formPath;
                this.zipLast.zipCodeCities = response.zipCodeCities || [];
                if (isCountryChange) {
                  this.formCountryInputChanged = false;
                  this.formCountryInputChangedBH$.next(formPath);
                }
                return this.zipLast.validation;
              }),
              catchError((error: Response) => {
                console.error(error);
                return from([{ invalidZipCode: true }]);
              })
            );
          }
        ),
        first()
      )
      .toPromise();

    this.zipValidationPromise.then((data) => {
      this.resetZipValidationPromise();
    });
  }

  zipCodeValidator(selectedCountryCode: string, formPath?: string): ValidatorFn {
    return (control: AbstractControl): Promise<ZipValidationErrors> => {
      const value: string = control.value;

      return this.zipCodeValidatorFn(value, selectedCountryCode, formPath);
    };
  }

  zipCodeValidatorFn(
    value: string,
    selectedCountryCode: string,
    formPath?: string,
    isCountryChange?: boolean
  ): Promise<ZipValidationErrors> {
    let validationError: Promise<{
      invalidZipCode: boolean;
    } | null> = of(null).toPromise();

    if (isCountryChange) {
      this.formCountryInputChanged = true;
    }
    if (selectedCountryCode) {
      if (
        this.zipLast &&
        this.zipLast.value === value &&
        this.zipLast.country === selectedCountryCode &&
        (formPath ? this.zipLast.formPath === formPath : true)
      ) {
        validationError = of(this.zipLast.validation).toPromise();
      }
      this.zipValidationSubject.next({
        selectedCountryCode,
        inputValue: value,
        formPath: formPath,
        isCountryChange: this.formCountryInputChanged ? true : false
      });

      validationError = this.zipValidationPromise;
    } else {
      this.zipLast.validation = null;
    }

    return validationError;
  }

  static emptySpacesValidator(): ValidatorFn {
    return (control: AbstractControl): RequiredValidationError => {
      return this.emptySpacesValidatorFn(control.value);
    };
  }

  static emptySpacesValidatorFn(value: string): RequiredValidationError {
    const noSpaceRegex = new RegExp('^\\s+$');

    let validationError: RequiredValidationError = null;

    if (noSpaceRegex.test(value)) {
      validationError = { required: true };
    }

    return validationError;
  }
}
